import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { listarEspecificaciones, actualizarIdentidadEspecificacion, listarCatalogo } from './api/catalogo';
import {
    Search, CheckCircle, AlertCircle,
    ChevronDown, ChevronUp, Save, RefreshCw, X, ExternalLink
} from 'lucide-react';
import { toast } from 'react-toastify';

// ── Helpers ───────────────────────────────────────────────────────────────────
const normalizeStr = (s = '') =>
    String(s).toUpperCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^A-Z0-9]/g, ' ').replace(/\s+/g, ' ').trim();

const tokenize = (s = '') =>
    normalizeStr(s).split(' ').filter(t => t.length >= 2);

const scoreMatch = (a = '', b = '') => {
    const ta = tokenize(a);
    const tb = new Set(tokenize(b));
    if (!ta.length) return 0;
    return ta.filter(t => tb.has(t)).length / ta.length;
};

// Score aprovechando tokens_busqueda del nuevo formato
const calcScore = (spec, modelo) => {
    const specVersion = spec.vehiculo?.version || spec.version || '';
    const specModelo = spec.vehiculo?.modelo || spec.modelo || '';
    const tokensBusq = spec.tokens_busqueda || [];
    const modelDetail = modelo.modelDetail || '';
    const modelNorm = normalizeStr(modelDetail);

    // Bonus si tokens_busqueda contiene la versión del modelo de la colección modelos
    const tokenBonus = tokensBusq.some(t =>
        normalizeStr(t) === modelNorm || modelNorm.includes(normalizeStr(t))
    ) ? 2 : 0;

    const full = `${modelo.model} ${modelDetail}`;
    const sModelo = scoreMatch(modelo.model, specModelo) * 2;
    const sVer = scoreMatch(modelDetail, specVersion);
    const sFull = scoreMatch(full, `${specModelo} ${specVersion}`);

    return tokenBonus + sModelo + sVer + sFull;
};

// Accesores seguros para la nueva estructura dual (raíz + vehiculo.*)
const getMarca = s => s.vehiculo?.marca || s.marca || '';
const getModelo = s => s.vehiculo?.modelo || s.modelo || '';
const getVersion = s => s.vehiculo?.version || s.version || '';
const fmt = (s = '') => String(s || '—');

const PAGE_SIZE = 20;

// ── Componente principal ──────────────────────────────────────────────────────
export default function SpecsMantenedor() {
    const navigate = useNavigate();

    const [specs, setSpecs] = useState([]);
    const [totalSpecs, setTotalSpecs] = useState(0);
    const [pagina, setPagina] = useState(0);
    const [loading, setLoading] = useState(false);

    const modelosCache = useRef({});
    const catalogoRef = useRef(null); // cache del catálogo completo — evita refetch por marca
    const [loadingModelos, setLoadingModelos] = useState(false);

    const [filtroMarca, setFiltroMarca] = useState('');
    const [filtroBusqueda, setFiltroBusqueda] = useState('');
    const [filtroEstado, setFiltroEstado] = useState('todos');
    const [marcas, setMarcas] = useState([]);

    const [editando, setEditando] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [guardando, setGuardando] = useState(false);
    const [expandido, setExpandido] = useState(null);

    // ── Modelos por marca con cache ───────────────────────────────────────────
    const getModelosPorMarca = useCallback(async (marca) => {
        if (!marca) return [];
        const key = normalizeStr(marca);
        if (modelosCache.current[key]) return modelosCache.current[key];

        setLoadingModelos(true);
        try {
            if (!catalogoRef.current) {
                const { autos } = await listarCatalogo({ todos: true });
                catalogoRef.current = autos;
            }
            const resultado = catalogoRef.current.filter(a => normalizeStr(a.marca) === key);
            modelosCache.current[key] = resultado;
            return resultado;
        } catch (e) {
            console.error('Error cargando modelos:', e);
            return [];
        } finally {
            setLoadingModelos(false);
        }
    }, []);

    // ── Enriquecer specs con scores ───────────────────────────────────────────
    const enriquecer = useCallback(async (specsRaw) => {
        const marcasNeeded = [...new Set(specsRaw.map(getMarca).filter(Boolean))];
        await Promise.all(marcasNeeded.map(getModelosPorMarca));

        return specsRaw.map(spec => {
            const key = normalizeStr(getMarca(spec));
            const candidatos = modelosCache.current[key] || [];
            let best = null, score = 0;
            for (const m of candidatos) {
                const s = calcScore(spec, m);
                if (s > score) { score = s; best = m; }
            }
            return { ...spec, _matchScore: score, _bestMatch: best, _hasMatch: score >= 0.5 };
        });
    }, [getModelosPorMarca]);

    // ── Cargar página ─────────────────────────────────────────────────────────
    const cargarPagina = useCallback(async (paginaIdx) => {
        setLoading(true);
        try {
            const { specs: raw, total, marcas: marcasDisponibles } = await listarEspecificaciones({
                marca: filtroMarca || undefined, page: paginaIdx,
            });
            setTotalSpecs(total);
            setMarcas(marcasDisponibles);

            const enriquecidos = await enriquecer(raw);
            setSpecs(enriquecidos);
            setPagina(paginaIdx);
        } catch (e) {
            console.error(e);
            toast.error('Error cargando fichas');
        } finally {
            setLoading(false);
        }
    }, [filtroMarca, enriquecer]);

    useEffect(() => {
        cargarPagina(0);
    }, [filtroMarca]); // eslint-disable-line

    const irSiguiente = () => cargarPagina(pagina + 1);
    const irAnterior = () => { if (pagina > 0) cargarPagina(pagina - 1); };

    // ── Filtrado client-side ──────────────────────────────────────────────────
    const specsFiltrados = specs.filter(s => {
        const txt = filtroBusqueda.toLowerCase();
        const cumpleTexto = !txt || [getMarca(s), getModelo(s), getVersion(s)]
            .some(v => (v || '').toLowerCase().includes(txt));
        const cumpleEstado =
            filtroEstado === 'todos' ? true :
                filtroEstado === 'ok' ? s._hasMatch : !s._hasMatch;
        return cumpleTexto && cumpleEstado;
    });

    const pageOk = specs.filter(s => s._hasMatch).length;
    const pageSinMatch = specs.length - pageOk;

    // ── Edición inline ────────────────────────────────────────────────────────
    const iniciarEdicion = (spec) => {
        setEditando(spec.id);
        setEditForm({ marca: getMarca(spec), modelo: getModelo(spec), version: getVersion(spec) });
        setExpandido(spec.id);
    };
    const cancelarEdicion = () => { setEditando(null); setEditForm({}); };

    const guardarEdicion = async (specId) => {
        setGuardando(true);
        try {
            await actualizarIdentidadEspecificacion(specId, editForm.marca.trim(), editForm.modelo.trim(), editForm.version.trim());
            delete modelosCache.current[normalizeStr(editForm.marca)];
            toast.success('Guardado ✅');
            setEditando(null);
            await cargarPagina(pagina);
        } catch (e) {
            console.error(e);
            toast.error('Error al guardar');
        } finally {
            setGuardando(false);
        }
    };

    const aplicarMatch = (spec, modelo) => {
        setEditando(spec.id);
        setEditForm({
            marca: modelo.marca.trim(),
            modelo: modelo.model.trim(),
            version: modelo.modelDetail.trim(),
        });
        setExpandido(spec.id);
    };

    // ── Render ────────────────────────────────────────────────────────────────
    const Paginacion = () => (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center' }}>
            <button onClick={irAnterior} disabled={pagina === 0 || loading} style={{ fontSize: 13, padding: '5px 14px' }}>
                ← Anterior
            </button>
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Página {pagina + 1}</span>
            <button onClick={irSiguiente} disabled={specs.length < PAGE_SIZE || loading} style={{ fontSize: 13, padding: '5px 14px' }}>
                Siguiente →
            </button>
        </div>
    );

    return (
        <div style={{ padding: '1.5rem', maxWidth: 1100, margin: '0 auto' }}>

            <div style={{ marginBottom: '1.5rem' }}>
                <h1 style={{ fontSize: 22, fontWeight: 500, margin: 0 }}>Mantenedor de Fichas Técnicas</h1>
                <p style={{ color: 'var(--color-text-secondary)', marginTop: 4, fontSize: 14 }}>
                    Normaliza marca, modelo y versión para mejorar el matching con <code>modelos</code>.
                </p>
            </div>

            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: '1.5rem' }}>
                {[
                    { label: 'Total fichas', value: totalSpecs, color: 'var(--color-text-primary)' },
                    { label: 'Con match (página)', value: pageOk, color: '#16a34a' },
                    { label: 'Sin match (página)', value: pageSinMatch, color: '#dc2626' },
                ].map(({ label, value, color }) => (
                    <div key={label} style={{ background: 'var(--color-background-secondary)', borderRadius: 8, padding: '10px 16px' }}>
                        <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{label}</div>
                        <div style={{ fontSize: 24, fontWeight: 500, color }}>{value}</div>
                    </div>
                ))}
            </div>

            {/* Filtros */}
            <div style={{ display: 'flex', gap: 10, marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
                    <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-secondary)' }} />
                    <input
                        placeholder="Buscar en esta página…"
                        value={filtroBusqueda}
                        onChange={e => setFiltroBusqueda(e.target.value)}
                        style={{ paddingLeft: 32, width: '100%', boxSizing: 'border-box' }}
                    />
                </div>
                <select value={filtroMarca} onChange={e => { setFiltroMarca(e.target.value); setFiltroBusqueda(''); }} style={{ minWidth: 160 }}>
                    <option value=''>Todas las marcas</option>
                    {marcas.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                <div style={{ display: 'flex', gap: 6 }}>
                    {[
                        { key: 'todos', label: 'Todos' },
                        { key: 'ok', label: '✅ Con match' },
                        { key: 'sin_match', label: '⚠️ Sin match' },
                    ].map(({ key, label }) => (
                        <button key={key} onClick={() => setFiltroEstado(key)} style={{
                            fontSize: 12, padding: '6px 12px',
                            background: filtroEstado === key ? 'var(--color-background-info)' : 'transparent',
                            color: filtroEstado === key ? 'var(--color-text-info)' : 'var(--color-text-secondary)',
                            border: `0.5px solid ${filtroEstado === key ? 'var(--color-border-info)' : 'var(--color-border-tertiary)'}`,
                            borderRadius: 6, cursor: 'pointer',
                        }}>{label}</button>
                    ))}
                </div>
                <button
                    onClick={() => { modelosCache.current = {}; catalogoRef.current = null; cargarPagina(0); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
                >
                    <RefreshCw size={14} /> Recargar
                </button>
            </div>

            {/* Info + paginación superior */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                    {loading || loadingModelos ? 'Cargando…' : `${specsFiltrados.length} de ${specs.length} en esta página`}
                </span>
                <Paginacion />
            </div>

            {/* Tabla */}
            {(loading || loadingModelos) ? (
                <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                    Cargando fichas…
                </div>
            ) : (
                <div style={{ border: '0.5px solid var(--color-border-tertiary)', borderRadius: 10, overflow: 'hidden' }}>
                    <div style={{
                        display: 'grid', gridTemplateColumns: '28px 1fr 1fr 1fr 1fr 90px',
                        gap: 8, padding: '8px 12px',
                        background: 'var(--color-background-secondary)',
                        fontSize: 11, fontWeight: 500, color: 'var(--color-text-secondary)',
                        textTransform: 'uppercase', letterSpacing: '0.05em',
                        borderBottom: '0.5px solid var(--color-border-tertiary)',
                    }}>
                        <div /><div>Marca</div><div>Modelo</div><div>Versión</div><div>Mejor match</div>
                        <div style={{ textAlign: 'center' }}>Acciones</div>
                    </div>

                    {specsFiltrados.length === 0 && (
                        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                            No hay resultados.
                        </div>
                    )}

                    {specsFiltrados.map((spec, idx) => {
                        const isEditando = editando === spec.id;
                        const isExpandido = expandido === spec.id;
                        const scorePct = Math.min(100, Math.round((spec._matchScore / 6) * 100));
                        const scoreColor = spec._matchScore >= 2 ? '#16a34a' : spec._matchScore >= 0.5 ? '#d97706' : '#dc2626';

                        return (
                            <div key={spec.id} style={{
                                borderBottom: idx < specsFiltrados.length - 1 ? '0.5px solid var(--color-border-tertiary)' : 'none',
                                background: isEditando ? 'var(--color-background-info)' : 'var(--color-background-primary)',
                            }}>
                                <div
                                    style={{ display: 'grid', gridTemplateColumns: '28px 1fr 1fr 1fr 1fr 90px', gap: 8, padding: '10px 12px', alignItems: 'center', cursor: 'pointer' }}
                                    onClick={() => setExpandido(isExpandido ? null : spec.id)}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                        {spec._hasMatch
                                            ? <CheckCircle size={15} color="#16a34a" />
                                            : <AlertCircle size={15} color="#dc2626" />}
                                    </div>

                                    <div style={{ fontSize: 13 }}>
                                        {isEditando
                                            ? <input value={editForm.marca} onChange={e => setEditForm(p => ({ ...p, marca: e.target.value }))} onClick={e => e.stopPropagation()} style={{ width: '100%', fontSize: 13, padding: '4px 8px' }} />
                                            : fmt(getMarca(spec))}
                                    </div>

                                    <div style={{ fontSize: 13 }}>
                                        {isEditando
                                            ? <input value={editForm.modelo} onChange={e => setEditForm(p => ({ ...p, modelo: e.target.value }))} onClick={e => e.stopPropagation()} style={{ width: '100%', fontSize: 13, padding: '4px 8px' }} />
                                            : fmt(getModelo(spec))}
                                    </div>

                                    <div style={{ fontSize: 12 }}>
                                        {isEditando
                                            ? <input value={editForm.version} onChange={e => setEditForm(p => ({ ...p, version: e.target.value }))} onClick={e => e.stopPropagation()} style={{ width: '100%', fontSize: 13, padding: '4px 8px' }} />
                                            : <span style={{ color: 'var(--color-text-secondary)' }}>{fmt(getVersion(spec))}</span>}
                                    </div>

                                    <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                                        {spec._bestMatch ? `${spec._bestMatch.model} ${spec._bestMatch.modelDetail}` : '—'}
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                                        <span style={{ fontSize: 11, fontWeight: 500, color: scoreColor, minWidth: 30, textAlign: 'right' }}>
                                            {scorePct}%
                                        </span>
                                        <button
                                            onClick={e => { e.stopPropagation(); navigate(`/admin/specs/${spec.id}`); }}
                                            title="Ver detalle completo"
                                            style={{ padding: '3px 6px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'center' }}
                                        >
                                            <ExternalLink size={13} />
                                        </button>
                                        {isExpandido
                                            ? <ChevronUp size={14} color="var(--color-text-secondary)" />
                                            : <ChevronDown size={14} color="var(--color-text-secondary)" />}
                                    </div>
                                </div>

                                {isExpandido && (
                                    <div style={{
                                        padding: '12px 12px 14px 40px',
                                        borderTop: '0.5px solid var(--color-border-tertiary)',
                                        background: 'var(--color-background-secondary)',
                                        display: 'flex', flexDirection: 'column', gap: 12,
                                    }}>
                                        <CandidatosTop
                                            spec={spec}
                                            modelos={modelosCache.current[normalizeStr(getMarca(spec))] || []}
                                            onAplicar={m => aplicarMatch(spec, m)}
                                        />
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            {!isEditando ? (
                                                <button onClick={e => { e.stopPropagation(); iniciarEdicion(spec); }} style={{ fontSize: 13 }}>
                                                    Editar identificación
                                                </button>
                                            ) : (
                                                <>
                                                    <button onClick={e => { e.stopPropagation(); guardarEdicion(spec.id); }} disabled={guardando}
                                                        style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, color: 'var(--color-text-success)', borderColor: 'var(--color-border-success)' }}>
                                                        <Save size={14} /> {guardando ? 'Guardando…' : 'Guardar'}
                                                    </button>
                                                    <button onClick={e => { e.stopPropagation(); cancelarEdicion(); }}
                                                        style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                                                        <X size={14} /> Cancelar
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <div style={{ marginTop: 12 }}><Paginacion /></div>
        </div>
    );
}

// ── Top candidatos ────────────────────────────────────────────────────────────
function CandidatosTop({ spec, modelos, onAplicar }) {
    const scored = modelos
        .map(m => ({ ...m, _score: calcScore(spec, m) }))
        .sort((a, b) => b._score - a._score)
        .slice(0, 5);

    if (!scored.length) return (
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
            No se encontraron candidatos para esta marca en <code>modelos</code>.
        </div>
    );

    return (
        <div>
            <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Top candidatos
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {scored.map((m, i) => {
                    const pct = Math.min(100, Math.round((m._score / 6) * 100));
                    const color = m._score >= 2 ? '#16a34a' : m._score >= 0.5 ? '#d97706' : '#dc2626';
                    return (
                        <div key={m.id || i} style={{
                            display: 'flex', alignItems: 'center', gap: 10, fontSize: 12,
                            padding: '5px 10px', borderRadius: 6,
                            background: 'var(--color-background-primary)',
                            border: '0.5px solid var(--color-border-tertiary)',
                        }}>
                            <span style={{ minWidth: 34, fontSize: 11, fontWeight: 500, color }}>{pct}%</span>
                            <span style={{ flex: 1 }}>{m.marca} / <strong>{m.model}</strong> / {m.modelDetail}</span>
                            <button onClick={e => { e.stopPropagation(); onAplicar(m); }} style={{ fontSize: 11, padding: '2px 8px' }}>
                                Usar →
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}